import {
  createFrontendPlugin,
  type FrontendPlugin,
} from '@backstage/frontend-plugin-api';
import { FormFieldBlueprint } from '@backstage/plugin-scaffolder-react/alpha';

/**
 * Custom scaffolder form fields.
 *
 * ADDITIVE, NOT A REPLACEMENT. FormFieldBlueprint attaches to the scaffolder's
 * form-fields API (`api:scaffolder/form-fields`), so the stock OwnerPicker,
 * EntityPicker and friends are untouched and still usable by any template. A
 * template opts in per field with `ui:field: GitHubUserPicker`.
 */
const gitHubUserPicker = FormFieldBlueprint.make({
  name: 'github-user-picker',
  params: {
    field: async () => {
      const { createFormField } = await import(
        '@backstage/plugin-scaffolder-react/alpha'
      );
      const { GitHubUserField } = await import('./GitHubUserField');
      return createFormField({
        name: 'GitHubUserPicker',
        component: GitHubUserField,
        // No `schema`. It expects a FieldSchema built by createFieldSchema, and
        // passing a raw JSON schema instead breaks the component's prop
        // inference. Omitting it is correct anyway: the value is a free-form
        // GitHub login, the template's own `type: string` already constrains it,
        // and the only authority on whether a login is valid is GitHub - which
        // the publish step reports loudly by rejecting the collaborator. A local
        // regex would only reject logins we guessed wrong about.
      });
    },
  },
});

// Sorted alphabetically by display name - see SortedOwnerField for why the
// stock OwnerPicker can't do this on its own.
const sortedOwnerPicker = FormFieldBlueprint.make({
  name: 'sorted-owner-picker',
  params: {
    field: async () => {
      const { createFormField } = await import(
        '@backstage/plugin-scaffolder-react/alpha'
      );
      const { SortedOwnerField } = await import('./SortedOwnerField');
      return createFormField({
        name: 'SortedOwnerPicker',
        component: SortedOwnerField,
      });
    },
  },
});

// Generic single-kind version of the above, for pickers that aren't
// ownership - e.g. the System field on create-service-group. Takes its kind
// from `ui:options.catalogFilter.kind`, same shape the stock EntityPicker
// already expects.
const sortedEntityPicker = FormFieldBlueprint.make({
  name: 'sorted-entity-picker',
  params: {
    field: async () => {
      const { createFormField } = await import(
        '@backstage/plugin-scaffolder-react/alpha'
      );
      const { SortedEntityField } = await import('./SortedEntityField');
      return createFormField({
        name: 'SortedEntityPicker',
        component: SortedEntityField,
      });
    },
  },
});

// The repository name box, composed from the System, Service Group and Service
// name fields on the same step - see RepositoryNameField.
const repositoryNamePicker = FormFieldBlueprint.make({
  name: 'repository-name-picker',
  params: {
    field: async () => {
      const { createFormField } = await import(
        '@backstage/plugin-scaffolder-react/alpha'
      );
      const { RepositoryNameField } = await import('./RepositoryNameField');
      return createFormField({
        name: 'RepositoryNamePicker',
        component: RepositoryNameField,
      });
    },
  },
});

const serviceNamePicker = FormFieldBlueprint.make({
  name: 'service-name-picker',
  params: {
    field: async () => {
      const { createFormField } = await import(
        '@backstage/plugin-scaffolder-react/alpha'
      );
      const { ServiceNameField } = await import('./ServiceNameField');
      return createFormField({
        name: 'ServiceNamePicker',
        component: ServiceNameField,
      });
    },
  },
});

// Live Snyk-org picker - see SnykOrgField for why this queries Snyk directly
// rather than the catalog (no Snyk-org catalog entity exists).
const snykOrgPicker = FormFieldBlueprint.make({
  name: 'snyk-org-picker',
  params: {
    field: async () => {
      const { createFormField } = await import(
        '@backstage/plugin-scaffolder-react/alpha'
      );
      const { SnykOrgField } = await import('./SnykOrgField');
      return createFormField({
        name: 'SnykOrgPicker',
        component: SnykOrgField,
      });
    },
  },
});

export const scaffolderFieldsPlugin: FrontendPlugin = createFrontendPlugin({
  pluginId: 'scaffolder-fields',
  extensions: [
    gitHubUserPicker,
    sortedOwnerPicker,
    sortedEntityPicker,
    repositoryNamePicker,
    serviceNamePicker,
    snykOrgPicker,
  ],
});
